import { Component, h, Host, Prop, Event, EventEmitter } from "@stencil/core";
import {
    handleOuterNavigation,
    handleOuterDropdownContainer,
} from "./navigation-controller/navigation-controller";
import applyElementPolyfills from "../../polyfills/element";
import "../../polyfills/node-list";
import { debounce } from "../../utils/debounce";
import waitForHydrated from "../../utils/waitForHydrated";
import { desktopBreakpoint } from "./shared-variables";

applyElementPolyfills();

@Component({
    tag: "ux-iws-nav",
    styleUrl: "navigation.scss",
    shadow: false,
})
export class Navigation {
    private baseClass: string = "ux-iws-nav";
    private baseElement: HTMLElement = document.querySelector(this.baseClass);
    private level1: HTMLUListElement | null = null;
    private level2: NodeListOf<HTMLUListElement> | null = null;
    private level3: NodeListOf<HTMLUListElement> | null = null;
    private level4: NodeListOf<HTMLUListElement> | null = null;
    private level1Items: NodeListOf<HTMLElement> | null = null;
    private level2Items: NodeListOf<HTMLElement> | null = null;
    private level3Items: NodeListOf<HTMLElement> | null = null;
    private level3Wrapper: NodeListOf<HTMLElement> | null = null;
    private level4Wrapper: NodeListOf<HTMLElement> | null = null;
    private level3OverviewLinks: NodeListOf<HTMLElement> | null = null;
    private level4OverviewLinks: NodeListOf<HTMLElement> | null = null;
    private subHeadlines: NodeListOf<HTMLElement> | null = null;
    private activeElement: HTMLElement | null = null;
    private lvl2Back: NodeListOf<HTMLElement> | null = null;
    private lvl3Back: NodeListOf<HTMLElement> | null = null;
    private lvl4Back: NodeListOf<HTMLElement> | null = null;
    private toolboxWrapper: NodeListOf<HTMLElement> | null = null;

    /** Back button text */
    @Prop() backButtonText: string = "Back";

    /** Navigation level change event */
    @Event({
        eventName: "uxIwsNavLevelChange",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navLevelChange: EventEmitter;

    /** emits calculated height of highest dropdown navigation */
    @Event({
        eventName: "uxIwsNavHighestSubNavigation",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navHighestSubNavigation: EventEmitter;

    /** emits local selection */
    @Event({
        eventName: "uxNavigationLocaleSelection",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navigationLocaleSelectionOpen: EventEmitter;

    componentDidLoad() {
        this.initStaticElements();
        this.addDynamicElements();
        this.addDynamicClasses();
        this.setStaticEvents();
        this.handleOverviewLinks();
        this.resizer();
    }

    /**
     * Init static elements
     */
    private initStaticElements = () => {
        this.level1 = this.baseElement.querySelector(".ux-iws-nav__lvl1");
        this.level2 = this.baseElement.querySelectorAll(".ux-iws-nav__lvl2");
        this.level3 = this.baseElement.querySelectorAll(".ux-iws-nav__lvl3");
        this.level4 = this.baseElement.querySelectorAll(".ux-iws-nav__lvl4");
        this.level1Items = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl1-item"
        );
        this.level2Items = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item"
        );
        this.level3Items = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-item"
        );
        this.level3Wrapper = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-wrapper"
        );
        this.level4Wrapper = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl4-wrapper"
        );
        this.level3OverviewLinks = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-item--overview"
        );
        this.level4OverviewLinks = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl4-item--overview"
        );
        this.subHeadlines = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-item--subheadline, .ux-iws-nav__lvl4-item--subheadline"
        );
        this.toolboxWrapper = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item-toolbox"
        );
        this.activeElement = this.level1;
    };

    /**
     * to keep the navigation html structure as simple as possible, additional elements are added dynamically
     */
    private addDynamicElements = () => {
        /**
         * add "go back" arrows and functionality for mobile navigation
         */
        const addBackLink = (items: NodeListOf<HTMLElement>, level: string) => {
            items.forEach((item) => {
                const mobileBack = document.createElement("span");
                mobileBack.className = `ux-iws-nav__lvl${level}-back`;
                mobileBack.textContent = this.backButtonText;
                item.prepend(mobileBack);
            });
        };
        addBackLink(this.level2, "2");
        addBackLink(this.level3Wrapper, "3");
        addBackLink(this.level4Wrapper, "4");

        this.lvl2Back = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-back"
        );
        this.lvl3Back = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl3-back"
        );
        this.lvl4Back = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl4-back"
        );

        /**
         * add chevron icon for items that contain children, displayed on mobile
         */
        [...this.level1Items, ...this.level2Items, ...this.level3Items].forEach(
            (item) => {
                if (item.children.length > 1) {
                    const arrowIcon = document.createElement("ux-icon");
                    arrowIcon.setAttribute("name", "chevron-right");
                    arrowIcon.setAttribute("weight", "normal");
                    item.firstElementChild.append(arrowIcon);
                }
            }
        );

        /**
         * add arrow icon for overview links
         */
        this.baseElement
            .querySelectorAll(
                `
                .ux-iws-nav__lvl2-item--overview a,
                .ux-iws-nav__lvl3-item--overview a,
                .ux-iws-nav__lvl4-item--overview a
                `
            )
            .forEach((item) => {
                const arrowIcon = document.createElement("ux-icon");
                arrowIcon.setAttribute("name", "arrow-right");
                arrowIcon.setAttribute("weight", "normal");
                item.append(arrowIcon);
            });
    };

    /**
     * to keep the navigation structure as simple as possible, additional classes are added dynamically
     * depending on the number of subitems respective classes are defined
     */
    private addDynamicClasses = () => {
        const addChildrenClass = (
            items: NodeListOf<HTMLElement>,
            level: string
        ) =>
            items.forEach((item) => {
                if (item.children.length > 1) {
                    item.classList.add(
                        `ux-iws-nav__lvl${level}-item--has-children`
                    );
                }
            });

        addChildrenClass(this.level1Items, "1");
        addChildrenClass(this.level2Items, "2");
        addChildrenClass(this.level3Items, "3");
    };

    /**
     * Static navigation event handling
     */
    private setStaticEvents = () => {
        this.setKeyboardNavigationEvents(this.level1Items);
        this.setKeyboardNavigationEvents(this.level2Items);
        this.setKeyboardNavigationEvents(this.level3Items);

        [...this.level1Items, ...this.level2Items].forEach((item) => {
            item.addEventListener("click", this.navigationForth);
        });

        [
            ...this.level2,
            ...this.level3,
            ...this.level4,
            ...this.level3Wrapper,
            ...this.level4Wrapper,
        ].forEach((item) => {
            item.addEventListener("click", this.stopBubbling);
        });

        [...this.lvl2Back, ...this.lvl3Back, ...this.lvl4Back].forEach(
            (item) => {
                item.addEventListener("click", this.navigationBack);
            }
        );

        this.subHeadlines.forEach((item) => {
            item.addEventListener("click", this.preventDefault);
        });

        this.baseElement.addEventListener("uxIwsNavOpen", () => {
            this.navigationOpen();
        });
        this.baseElement.addEventListener("uxIwsNavClose", () => {
            this.navigationClose();
        });

        this.baseElement.addEventListener(
            "uxToolboxLoaded",
            (event: CustomEvent) => {
                if (event.detail.success == true) {
                    if (window.innerWidth < desktopBreakpoint) {
                        this.setHighestSubNavigation();
                    }
                    const teaserIcon = this.activeElement.querySelector(
                        "ux-iws-teaser-sidebar-icon:last-child"
                    ) as HTMLElement;
                    waitForHydrated(teaserIcon, () => {
                        handleOuterDropdownContainer(
                            this.activeElement.offsetHeight
                        );
                    });
                }
            }
        );

        document.body.addEventListener("click", (e) => {
            const target = e.target as Node;
            const header = this.baseElement.closest(
                ".ux-iws-header"
            ) as HTMLElement;
            if (header && !header.contains(target)) {
                this.navigationClose();
            }
        });
        window.addEventListener("resize", this.resizer);
    };

    /**
     * Accessibility
     * Add event handling for keyboard
     */
    private setKeyboardNavigationEvents = (level: NodeListOf<HTMLElement>) => {
        level.forEach((item) => {
            if (item.children.length > 1) {
                item.addEventListener("click", (e: Event) => {
                    e.preventDefault();
                    this.navigationForth(e);
                });
            }
        });
    };

    /**
     * Prevent Default
     */
    private preventDefault = (e: Event) => {
        e.preventDefault();
    };

    /**
     * Stop bubbling
     */
    private stopBubbling = (e: Event) => {
        e.stopPropagation();
    };

    /**
     * Navigation dive in
     */
    private navigationForth = (e: Event) => {
        let target = e.target as HTMLElement;
        if (!target) {
            return;
        }
        if (target.tagName === "A") {
            target = target.parentElement;
        }
        const currentLevel = (() => {
            const parentClass = target.parentElement.className;
            if (parentClass.includes("lvl1")) {
                return "2";
            } else if (parentClass.includes("lvl2")) {
                return "3";
            } else {
                return "4";
            }
        })();
        const targetChildQuerySelector = (() => {
            if (currentLevel == "2") {
                return ".ux-iws-nav__lvl2";
            } else if (currentLevel == "3") {
                return ".ux-iws-nav__lvl3-wrapper";
            } else {
                return ".ux-iws-nav__lvl4-wrapper";
            }
        })();
        const targetChild = target.querySelector(
            targetChildQuerySelector
        ) as HTMLElement;
        if (target.className.includes("has-children")) {
            this.activeElement = targetChild;
            this.stopBubbling(e);
            this.handleActiveStateClasses(target, currentLevel, "add");
            this.handleToolbox(currentLevel, "open");
            if (window.innerWidth < desktopBreakpoint) {
                this.setHighestSubNavigation();
            }
            handleOuterNavigation(currentLevel, "open");
            handleOuterDropdownContainer(this.activeElement.offsetHeight);
            this.windowScrollTop();
            this.navLevelChange.emit({
                currentLevel,
            });
        } else {
            const backButtonLvl3 =
                target.parentElement.parentElement.querySelector(
                    ".ux-iws-nav__lvl3-back"
                ) as HTMLElement;
            if (currentLevel == "3" && backButtonLvl3 !== null) {
                backButtonLvl3.click();
            }
            const backButtonLvl4 =
                target.parentElement.parentElement.querySelector(
                    ".ux-iws-nav__lvl4-back"
                ) as HTMLElement;
            if (currentLevel == "4" && backButtonLvl4 !== null) {
                backButtonLvl4.click();
            }
        }
    };

    /**
     * Mobile menu go step back level 2
     */
    private navigationBack = (e) => {
        const target = e.target as HTMLElement;
        const targetParent = target.parentElement.parentElement
            .parentElement as HTMLElement;
        const parentClass = targetParent.className;
        const currentLevel = (() => {
            if (parentClass.includes("ux-iws-nav__lvl1")) {
                return "1";
            } else if (parentClass.includes("ux-iws-nav__lvl2")) {
                return "2";
            } else {
                return "3";
            }
        })();
        this.activeElement = targetParent.matches(".ux-iws-nav__lvl3")
            ? targetParent.closest(".ux-iws-nav__lvl3-wrapper")
            : targetParent;
        this.handleActiveStateClasses(target, currentLevel, "remove");
        this.handleToolbox(currentLevel, "close");
        if (window.innerWidth < desktopBreakpoint) {
            this.setHighestSubNavigation();
        }
        handleOuterNavigation(currentLevel, "open");
        handleOuterDropdownContainer(this.activeElement.offsetHeight);
        this.navLevelChange.emit({
            currentLevel,
        });
    };

    /**
     * Open navigation
     */
    private navigationOpen = () => {
        const currentLevel = "1";
        this.activeElement = this.level1;
        handleOuterNavigation(currentLevel, "open");
        this.windowScrollTop();
    };

    /**
     * Close navigation
     */
    private navigationClose = () => {
        const currentLevel = "1";
        this.activeElement = this.level1;
        this.handleActiveStateClasses(null, currentLevel, "remove");
        handleOuterNavigation(currentLevel, "close");
        this.navigationLocaleSelectionOpen.emit({ isOpen: false });
    };

    /**
     * handle active state classes
     * @param target
     * @param currentLevel
     * @param type
     */
    private handleActiveStateClasses = (
        target: HTMLElement,
        currentLevel: string,
        type: "add" | "remove"
    ) => {
        if (type == "add") {
            if (currentLevel !== "4") {
                this.level2Items.forEach((item) => {
                    item.classList.remove("ux-iws-nav--active");
                    item.classList.remove("ux-iws-nav--not-active");
                });
            }
            this.level3Items.forEach((item) => {
                item.classList.remove("ux-iws-nav--active");
                item.classList.remove("ux-iws-nav--not-active");
            });
            switch (currentLevel) {
                case "2":
                    this.level1Items.forEach((item) => {
                        item.classList.remove("ux-iws-nav--active");
                        item.classList.add("ux-iws-nav--not-active");
                    });
                    break;
                case "3":
                    target.parentElement
                        .querySelectorAll(".ux-iws-nav__lvl2-item")
                        .forEach((item) => {
                            item.classList.add("ux-iws-nav--not-active");
                        });
                    break;
                default:
                    target.parentElement
                        .querySelectorAll(".ux-iws-nav__lvl3-item")
                        .forEach((item) => {
                            item.classList.add("ux-iws-nav--not-active");
                        });
                    break;
            }
            target.classList.remove("ux-iws-nav--not-active");
            target.classList.add("ux-iws-nav--active");
        } else {
            switch (currentLevel) {
                case "1":
                    [...this.level1Items, ...this.level2Items].forEach(
                        (item) => {
                            item.classList.remove("ux-iws-nav--active");
                            item.classList.remove("ux-iws-nav--not-active");
                        }
                    );
                    break;
                case "2":
                    this.level2Items.forEach((item) => {
                        item.classList.remove("ux-iws-nav--active");
                        item.classList.remove("ux-iws-nav--not-active");
                    });
                    break;
                default:
                    this.level3Items.forEach((item) => {
                        item.classList.remove("ux-iws-nav--active");
                        item.classList.remove("ux-iws-nav--not-active");
                    });
                    break;
            }
        }
    };

    /**
     * Set desktop level 2, level 3 and level 4 navigation height based on the element with the most subitems
     * if toolbox content is higher than navigation content, use toolbox inner height
     */
    private setHighestSubNavigation = () => {
        const navigationHeightArray = [];
        const toolboxItems = this.baseElement.querySelectorAll(
            ".ux-iws-nav__lvl2-item-toolbox"
        );
        const hasToolbox = toolboxItems.length > 0;
        const headerHeight = 100;
        const toolboxGutter = 35;
        const toolboxHeightNoGutter = 125;

        this.level2.forEach((item) => {
            item.style.height = "auto";
            navigationHeightArray.push(item.offsetHeight);
        });
        this.level3.forEach((item) => {
            item.style.height = "auto";
            const height = hasToolbox
                ? item.offsetHeight + toolboxHeightNoGutter
                : item.offsetHeight;
            navigationHeightArray.push(height);
        });
        toolboxItems.forEach((toolboxItem: HTMLUListElement) => {
            toolboxItem.style.marginTop = "0";
        });
        const highestNavigation = Math.max(...navigationHeightArray);
        this.navHighestSubNavigation.emit(highestNavigation);
        /** navigationFits = the viewport is tall enought that
         * all the navigation points can be displayed with the same height */
        const navigationFits =
            highestNavigation + headerHeight < window.innerHeight;
        this.level2.forEach((item) => {
            if (window.innerWidth < desktopBreakpoint) {
                item.style.height = "auto";
            } else {
                if (navigationFits) {
                    item.style.height = highestNavigation + "px";
                } else {
                    const itemLevel3 = item.querySelector(
                        ".ux-iws-nav__lvl3"
                    ) as HTMLElement;
                    const hasLevel3 = !!itemLevel3;
                    const highestHeight = hasLevel3
                        ? Math.max(
                              itemLevel3.offsetHeight + toolboxHeightNoGutter,
                              item.offsetHeight
                          )
                        : item.offsetHeight;
                    if (highestHeight + headerHeight > window.innerHeight) {
                        item.style.height =
                            window.innerHeight - headerHeight + "px";
                        item.style.overflowY = "auto";
                        item.style.overflowX = "hidden";
                    } else {
                        item.style.height = highestHeight + "px";
                    }
                }
            }
        });
        this.level3.forEach((item) => {
            if (window.innerWidth < desktopBreakpoint) {
                item.style.height = "auto";
            } else {
                if (navigationFits) {
                    const height = hasToolbox
                        ? highestNavigation - toolboxHeightNoGutter
                        : highestNavigation;
                    item.style.height = height + "px";
                } else {
                    const lv2Ul = item.closest(
                        ".ux-iws-nav__lvl2"
                    ) as HTMLElement;
                    const lvl2Height = hasToolbox
                        ? lv2Ul.scrollHeight - toolboxHeightNoGutter
                        : lv2Ul.scrollHeight;
                    const siblings = Array.from(
                        lv2Ul.querySelectorAll(".ux-iws-nav__lvl3")
                    );
                    const heights = [];
                    siblings.forEach((sibling: HTMLElement) =>
                        heights.push(sibling.offsetHeight)
                    );
                    const maxHeight = Math.max(lvl2Height, ...heights);
                    item.style.height = maxHeight + "px";
                }
            }
        });
        this.level4.forEach((item) => {
            if (window.innerWidth < desktopBreakpoint) {
                item.style.height = "auto";
            } else {
                if (navigationFits) {
                    const height = hasToolbox
                        ? highestNavigation - toolboxHeightNoGutter
                        : highestNavigation;
                    item.style.height = height + "px";
                } else {
                    const itemLevel3 = item.closest(
                        ".ux-iws-nav__lvl3"
                    ) as HTMLElement;
                    const lvl3Height = hasToolbox
                        ? itemLevel3.scrollHeight - toolboxHeightNoGutter
                        : itemLevel3.scrollHeight;
                    const siblings = Array.from(
                        itemLevel3.querySelectorAll(".ux-iws-nav__lvl4")
                    );
                    const heights = [];
                    siblings.forEach((sibling: HTMLElement) =>
                        heights.push(sibling.offsetHeight)
                    );
                    const maxHeight = Math.max(lvl3Height, ...heights);
                    item.style.height = maxHeight + "px";
                }
            }
        });
        /** managing the margin top of each toolbox when the navigation fits */
        if (
            hasToolbox &&
            navigationFits &&
            window.innerWidth >= desktopBreakpoint
        ) {
            toolboxItems.forEach((toolboxItem: HTMLElement) => {
                const toolboxRects = toolboxItem.getBoundingClientRect();
                const toolboxParentRects =
                    toolboxItem.parentElement.getBoundingClientRect();
                if (toolboxParentRects.bottom - toolboxRects.bottom > 1) {
                    toolboxItem.style.marginTop =
                        toolboxParentRects.bottom - toolboxRects.bottom + "px";
                }
            });
        } else if (
            hasToolbox &&
            !navigationFits &&
            window.innerWidth >= desktopBreakpoint
        ) {
            toolboxItems.forEach((toolboxItem: HTMLElement) => {
                const parentScrollHeight =
                    toolboxItem.parentElement.scrollHeight;
                const level3ScrollHeight =
                    toolboxItem.parentElement.querySelector(
                        ".ux-iws-nav__lvl3"
                    )?.scrollHeight;
                const highestScrollHeight =
                    level3ScrollHeight >
                    parentScrollHeight - toolboxHeightNoGutter
                        ? level3ScrollHeight + toolboxHeightNoGutter
                        : parentScrollHeight;
                const toolboxOffsetTop = toolboxItem.offsetTop;
                const marginTop =
                    highestScrollHeight -
                    toolboxOffsetTop -
                    toolboxHeightNoGutter -
                    toolboxGutter;
                toolboxItem.style.marginTop = marginTop + "px";
            });
        }
    };

    /**
     * Handle positioning of overview Links
     */
    private handleOverviewLinks = () => {
        this.level3OverviewLinks.forEach((item) => {
            item.closest(".ux-iws-nav__lvl3-wrapper").classList.add(
                "ux-iws-nav__lvl3-wrapper--has-overview-links"
            );
        });
        this.level4OverviewLinks.forEach((item) => {
            item.closest(".ux-iws-nav__lvl4-wrapper").classList.add(
                "ux-iws-nav__lvl4-wrapper--has-overview-links"
            );
        });
    };

    /**
     * Load toolbox
     * Hide toolbox level 3 mobile
     */
    private handleToolbox = (currentLevel, type) => {
        if (type == "open") {
            if (currentLevel == "2") {
                const activeToolbox = this.activeElement.querySelector(
                    ".ux-iws-meta-navigation-toolbox"
                );
                if (activeToolbox !== null) {
                    activeToolbox.setAttribute("active", "true");
                }

                this.toolboxWrapper.forEach((item) => {
                    item.classList.add("ux-iws-nav__lvl2-item-toolbox--show");
                });
            } else {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.remove(
                        "ux-iws-nav__lvl2-item-toolbox--show"
                    );
                });
            }
        } else {
            if (currentLevel == "1") {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.remove(
                        "ux-iws-nav__lvl2-item-toolbox--show"
                    );
                });
            } else {
                this.toolboxWrapper.forEach((item) => {
                    item.classList.add("ux-iws-nav__lvl2-item-toolbox--show");
                });
            }
        }
    };

    /**
     * Always scroll to top on navigation open
     */
    private windowScrollTop = () => {
        window.scrollTo(0, 0);
    };

    /**
     * browser resizing
     * used for different event handling mobile vs. desktop
     * used for level 2 and level 3 navigation height
     * used for dropdown container vertical offset
     * used for navigation close level 1 on desktop
     * debounced resizer -> 10ms
     */
    private resizer = debounce(() => {
        this.setHighestSubNavigation();
        handleOuterDropdownContainer(this.activeElement.offsetHeight);

        if (
            document.body.classList.contains("ux-iws-nav-active--lvl-1") ==
                true &&
            window.innerWidth >= desktopBreakpoint
        ) {
            this.navigationClose();
        }
    }, 10);

    /**
     * Render navigation
     */
    render() {
        return (
            <Host class={`${this.baseClass}`}>
                <slot name="navigation" />
                <slot name="navigation-meta" />
                <slot name="navigation-eyecatcher" />
            </Host>
        );
    }
}
